import React from "react"
import { DeliveryOrderLine, Order, OrderRefund, Reason } from "lib/types/generated/graphql-types"
import { Id, Name, ProductImage, RefundButtonWrapper, RefundRow } from "../Shared.styled"
import SecondaryButton from "../../../../Ui/Buttons/SecondaryButton"
import { useMutation } from "@apollo/client"
import CREATE_REFUND from "../../../../../graphql/mutations/order/refunds/CreateRefund"
import Money from "../../../../Money"
import DineroFactory from "dinero.js"
import UPDATE_REFUND from "../../../../../graphql/mutations/order/refunds/UpdateRefund"
import { ReactComponent as RefundIcon } from "../../../../../images/icons/arrow-rotate-left-thick.svg"
import Status from "../../../../Ui/Status"
import { getRefundInput } from "../Helpers/helpers"
import { handleErrorMessages } from "helpers/errors"
import { handleImageError } from "helpers/image"
import { Refunds } from "../../../../../classes/order/Refunds"
import { Compensations } from "../../../../../classes/order/Compensations"
import { Compensated, RefundRowContainer } from "../../CompensationOrder/Shared.styled"

type Props = {
  orderLine: DeliveryOrderLine
  order: Order
  currencyCode: string
  refunds: OrderRefund[]
  refetch: () => void
  reason: Reason
}

export const VariantRow = ({ orderLine, order, currencyCode, refetch, reason }: Props) => {
  const refunds = new Refunds(order.Refunds)
  const compensations = new Compensations(order.Compensations)

  const orderLineRefunded = refunds
    .completedRefunds()
    .map((refund) => refund.orderLines.find((ol) => ol.orderLineId === orderLine.orderLineId))

  const getRefundedQuantity = () =>
    orderLineRefunded.reduce((a, b) => {
      return a + (b?.quantity ?? 0)
    }, 0)

  const [createRefund, { loading: createLoading }] = useMutation(CREATE_REFUND, {
    onCompleted: () => {
      refetch()
    },
    onError: (error) => {
      handleErrorMessages(error)
    }
  })

  const [updateRefund, { loading: updateLoading }] = useMutation(UPDATE_REFUND, {
    onCompleted: () => {
      refetch()
    },
    onError: (error) => {
      handleErrorMessages(error)
    }
  })

  const orderLineExistInRefund = (id: string) => {
    return refunds.getPendingRefund()?.orderLines.find((ol) => ol.orderLineId === id)
  }

  const getOrderLine = () => {
    return order?.orderLines?.find((ol) => ol.id === orderLine.orderLineId)
  }

  const orderLineCompensatedAmount = compensations.orderLineTotal(orderLine.orderLineId)

  const createOrUpdateOrderRefund = () => {
    if (!refunds.pending()) {
      return createRefund({
        variables: {
          orderId: order.id,
          input: {
            ...(reason?.cause && reason?.code && { reason }),
            orderLines: {
              orderLineId: orderLine.orderLineId,
              quantity: orderLine.quantity - getRefundedQuantity()
            }
          }
        }
      })
    }
    return updateRefund({
      variables: {
        refundId: refunds.getPendingRefund()?.id,
        input: {
          ...getRefundInput(refunds.getPendingRefund()),
          orderLines: [
            ...refunds.getPendingRefund()?.orderLines?.map((ol) => ({
              orderLineId: ol.orderLineId,
              quantity: ol.quantity
            })),
            {
              orderLineId: orderLine.orderLineId,
              quantity: orderLine.quantity - getRefundedQuantity()
            }
          ]
        }
      }
    })
  }

  return (
    <RefundRowContainer>
      {orderLineCompensatedAmount > 0 && (
        <>
          <Compensated>
            Previously compensated amount:{" "}
            <Money
              amount={orderLineCompensatedAmount}
              currencyUnit={order.currencyCode as DineroFactory.Currency}
            />
          </Compensated>
          <hr />
        </>
      )}
      <RefundRow key={orderLine.orderLineId}>
        <ProductImage src={getOrderLine()?.imageUrl as string} onError={handleImageError} />
        <div>
          <Name>{getOrderLine()?.name}</Name>
          <Money
            amount={orderLine.totalAmount}
            currencyUnit={currencyCode as DineroFactory.Currency}
          ></Money>

          <div>Order quantity: {orderLine.quantity}</div>
          {getRefundedQuantity() > 0 && <div>Refunded quantity: {getRefundedQuantity()}</div>}
          <Id>
            <b>Variant ID:</b> <span>{getOrderLine()?.productVariantId}</span>
          </Id>
        </div>
        <RefundButtonWrapper>
          {getRefundedQuantity() === orderLine.quantity ? (
            <Status status="Refunded" />
          ) : (
            <SecondaryButton
              handleClick={createOrUpdateOrderRefund}
              disabled={!!orderLineExistInRefund(orderLine.orderLineId)}
              loading={createLoading || updateLoading}
            >
              <RefundIcon />
              Refund
            </SecondaryButton>
          )}
          <span>
            {" "}
            Total :{" "}
            <b>
              {" "}
              <Money
                amount={orderLine.totalAmount}
                currencyUnit={currencyCode as DineroFactory.Currency}
              />
            </b>
          </span>
        </RefundButtonWrapper>
      </RefundRow>
    </RefundRowContainer>
  )
}
